import { useState } from "react";
import { Form } from "react-bootstrap";

/**
 * Reusable tags/chips input.
 *
 * Tags are committed on Enter, comma, blur or paste. Each tag becomes a
 * removable badge. Whitespace is trimmed and duplicates (case-insensitive)
 * are ignored. Backspace on an empty input removes the last tag.
 *
 * Controlled component — the source of truth is the `tags` array prop.
 *
 * @param {object} props
 * @param {string[]} props.tags - current tags.
 * @param {(next: string[]) => void} props.onChange - emits the next tags array.
 * @param {string} [props.label]
 * @param {string} [props.id]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.required]
 * @param {boolean} [props.isInvalid]
 * @param {string} [props.feedback]
 * @param {() => void} [props.onBlur]
 */
const TagsInput = ({
  tags = [],
  onChange,
  label = "Tags",
  id = "tags",
  placeholder = "Type a tag and press Enter or comma",
  required = false,
  isInvalid = false,
  feedback,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState("");

  const addTags = (raw) => {
    const candidates = raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    if (candidates.length === 0) return;

    const next = [...tags];
    candidates.forEach((tag) => {
      const exists = next.some((t) => t.toLowerCase() === tag.toLowerCase());
      if (!exists) next.push(tag);
    });

    if (next.length !== tags.length) onChange(next);
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const commitInput = () => {
    if (inputValue.trim()) {
      addTags(inputValue);
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    // Auto-commit when the user types a comma anywhere in the value.
    if (value.includes(",")) {
      addTags(value);
      setInputValue("");
    } else {
      setInputValue(value);
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (text.includes(",")) {
      e.preventDefault();
      addTags(text);
    }
  };

  const handleBlur = () => {
    commitInput();
    if (onBlur) onBlur();
  };

  return (
    <Form.Group>
      <Form.Label htmlFor={id}>
        {label}
        {required && <i className="bx bxs-asterisk text-danger ms-1" />}
      </Form.Label>
      <div
        className={`form-control d-flex flex-wrap align-items-center gap-2 my-2 h-auto ${
          isInvalid ? "is-invalid border-danger" : ""
        }`}
        style={{ minHeight: "40px", cursor: "text" }}
        onClick={() => document.getElementById(id)?.focus()}
      >
        {tags.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="badge bg-primary d-inline-flex align-items-center"
            style={{ fontSize: "0.8rem" }}
          >
            {tag}
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              style={{ fontSize: "0.55rem" }}
              aria-label={`Remove ${tag}`}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(index);
              }}
            />
          </span>
        ))}
        <input
          id={id}
          type="text"
          className="border-0 flex-grow-1 p-0"
          style={{ outline: "none", minWidth: "120px", background: "transparent" }}
          placeholder={tags.length === 0 ? placeholder : ""}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
      </div>
      {isInvalid && feedback && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {feedback}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default TagsInput;
